import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { templatesApi, type PromptTemplate, type TemplateKind } from "../api/templates";
import { Alert, Button, Card, Spinner, Textarea } from "../components/ui";

const KEY = ["templates"];

/**
 * Your own copies of the four prompt templates.
 *
 * These are seeded from the house defaults at sign-up and are yours to change
 * — editing one never affects anyone else. Saving bumps the template version,
 * which marks the documents it produces stale rather than silently changing
 * the next thing you generate.
 */
export default function Templates() {
  const [kind, setKind] = useState<TemplateKind>("resume");
  const { data, isPending } = useQuery({ queryKey: KEY, queryFn: templatesApi.list });

  if (isPending || !data) return <Spinner label="Loading templates…" />;

  const selected = data.find((t) => t.kind === kind) ?? data[0];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Prompt templates</h1>
        <p className="mt-1 text-sm text-gray-600">
          What Claude is told to produce at each step. These are your own copies — changing
          one affects only your documents.
        </p>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-3">
        {data.map((template) => (
          <button
            key={template.kind}
            type="button"
            onClick={() => setKind(template.kind)}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
              (template.kind === selected.kind
                ? "bg-accent-50 text-accent-700"
                : "text-gray-600 hover:bg-gray-100")
            }
          >
            {template.label}
            {!template.is_default && " ·"}
          </button>
        ))}
      </div>

      <TemplateEditor key={selected.kind} template={selected} />
    </div>
  );
}

function TemplateEditor({ template }: { template: PromptTemplate }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState(template.body_md);

  useEffect(() => setBody(template.body_md), [template.body_md]);

  const settle = () => queryClient.invalidateQueries({ queryKey: KEY });

  const save = useMutation({
    mutationFn: () => templatesApi.save(template.kind, body),
    onSuccess: settle,
  });
  const reset = useMutation({
    mutationFn: () => templatesApi.reset(template.kind),
    onSuccess: settle,
  });

  const dirty = body !== template.body_md;

  return (
    <Card>
      <div className="space-y-4">
        {dirty && (
          <Alert kind="error">
            Saving marks every document this template produces stale, so you can regenerate
            them against the new wording.
          </Alert>
        )}

        <Textarea
          rows={26}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          spellCheck={false}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save template"}
          </Button>

          {!template.is_default && (
            <Button
              variant="secondary"
              onClick={() => {
                if (confirm("Discard your changes and restore the original template?")) {
                  reset.mutate();
                }
              }}
              disabled={reset.isPending}
            >
              Reset to the original
            </Button>
          )}

          <span className="text-xs text-gray-500">
            Version {template.version}
            {template.is_default ? " · unchanged from the original" : " · customised"}
          </span>
        </div>
      </div>
    </Card>
  );
}
